import { createContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import api from '../utils/api';
import { toEng } from '../utils/toEng';
import { API_ENDPOINT_V1 } from '../utils/url';

export const GraphContext = createContext();

export const GraphProvider = ({ children }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const background3D = '#222333';
  const background2D = '#FFFFFF';
  const wrapperContainerWidth = document.querySelector(
    '.mainGraph_mainContainer__3BYgl'
  )?.offsetWidth;
  const wrapperContainerHeight = document.querySelector(
    '.mainGraph_mainContainer__3BYgl'
  )?.offsetHeight;
  const labelColors = {
    Person: {
      r: 248,
      g: 203,
      b: 88,
      hex: '#ffb500',
    },
    Company: {
      r: 248,
      g: 88,
      b: 116,
      hex: '#f94100',
    },
    Political_Party: {
      r: 31,
      g: 138,
      b: 192,
      hex: '#1f8ac0',
    },
    Event: {
      r: 50,
      g: 180,
      b: 50,
      hex: '#32b432',
    },
    Ltd: {
      r: 111,
      g: 45,
      b: 168,
      hex: '#6F2DA8',
    },
    Jsc: {
      r: 227,
      g: 137,
      b: 185,
      hex: '#E389B9',
    },
    Npo: {
      r: 40,
      g: 139,
      b: 168,
      hex: '#288BA8',
    },
  };
  const currentProjectId = window.location.pathname.split('/')[1];
  const [currentProjectName, setCurrentProjectName] = useState('');

  const [graphData, setGraphData] = useState();
  const [activeNode, setActiveNode] = useState({});
  const [specificNode, setSpecificNode] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [is3DActive, setIs3DActive] = useState(false);

  const [initialNodeCoordinates, setInitialNodeCoordinates] = useState();
  const [triggerActiveNodeFocus, setTriggerActiveNodeFocus] = useState();
  const [currentGraphPosition, setCurrentGraphPosition] = useState({});

  const open = Boolean(anchorEl);

  const fg2DRef = useRef();

  const setCameraPosition = (x, y, zoom) => {
    setCurrentGraphPosition({ x, y, zoom });
    fg2DRef.current.centerAt(x, y);
    fg2DRef.current.zoom(zoom);
  };

  const translateToEng = (node, label) => {
    const labels = ['Person', 'Company', 'Event'];
    if (labels.includes(node.label)) {
      return toEng(node.tooltip_ge.trim(), { capFirstLetters: true });
    }
    if (label === 'link') {
      if (node.comment || node.custom) return node.tooltip_ge;
      return t(node.tooltip_ge.trim());
    }
    if (node.label === 'Political_Party') {
      return t(node.tooltip_ge.trim());
    }
    return t(node.tooltip_ge);
  };

  async function fetchAllData() {
    setIsLoading(true);
    setActiveNode({});
    setSpecificNode('');
    let graphDataReturned;
    try {
      const currentProjectId = window.location.pathname.split('/')[1];
      const data = await api.get(
        `${API_ENDPOINT_V1}/graph/${currentProjectId}/get-all`
      );
      setGraphData({
        nodes: data.nodes.map((node) => {
          node.tooltip_en = translateToEng(node);
          return node;
        }),
        links: data.links.map((link) => {
          link.tooltip_en = translateToEng(link, 'link');
          const specialRels = [
            'Same_Email, Same_Address',
            'Organizer',
            'Organizator',
            'Participant',
          ];
          if (specialRels.includes(link.tooltip_ge.trim())) {
            link.tooltip_ge = t(`${link.tooltip_ge}`);
          }
          return link;
        }),
      });
      // Saving initial node coordinates
      setTimeout(() => {
        const initialCoordinates = data.nodes.map((node) => ({
          nodeIndex: node.index,
          nodeId: node.id,
          x: node.x,
          y: node.y,
        }));
        setInitialNodeCoordinates(initialCoordinates);
      }, 2500);
      graphDataReturned = data;
    } catch (error) {
      console.log('Error caught', error);
    }
    setIsLoading(false);
    return graphDataReturned;
  }

  function setSpecificNodeFromSearch(text) {
    if (Number.isNaN(parseInt(text))) {
      let labelTypes = ['Person', 'Company', 'Event', 'Political_Party'];
      if (!labelTypes.includes(text)) text = text.toLowerCase();
      const node = graphData.nodes.find(
        (item) =>
          item.tooltip_en.toLowerCase() === text ||
          item.tooltip_ge.toLowerCase() === text
      );
      if (node) return setSpecificNode(node.id);
      setSpecificNode(text);
    } else {
      const PersonNode = graphData.nodes.find(
        (item) => item.properties.id === text
      );
      const CompanyNode = graphData.nodes.find(
        (item) => item.properties.id === text
      );
      if (PersonNode) return setSpecificNode(PersonNode.id);
      if (CompanyNode) return setSpecificNode(CompanyNode.id);
    }
  }

  // Expands activeNode
  async function handleGoDeep() {
    try {
      const label = activeNode.label.toLowerCase();
      setIsLoading(true);
      let res = await api.post(
        `${API_ENDPOINT_V1}/${label}/${currentProjectId}/add`,
        {
          id: activeNode.properties.id,
          source: activeNode.properties.source,
          watchlist: false,
        }
      );
      dispatch({
        type: 'SNACKBAR_PRINT',
        payload: {
          type: 'success',
          text: res.message,
        },
      });
      const returnedGraphData = await fetchAllData();
      const newActiveNode = returnedGraphData.nodes.find(
        (item) => item.properties.id === activeNode.properties.id
      );
      setActiveNode(newActiveNode);
      setTimeout(() => {
        setTriggerActiveNodeFocus(true);
      }, 2000);
      setIsLoading(false);
    } catch (ex) {
      setIsLoading(false);
      dispatch({
        type: 'SNACKBAR_PRINT',
        payload: {
          type: 'error',
          text:
            ex.response.data.error || 'Oops, something went wrong, try later!',
        },
      });
      await fetchAllData();
    }
  }
  const projectId = window.location.pathname.split('/')[1];

  const removeFromWatchlist = async (id, label) => {
    await api.post(`${API_ENDPOINT_V1}/graph/watchlist-remove`, {
      id,
      label,
      projectId,
    });
    const newGraphData = { ...graphData };
    newGraphData.nodes.forEach((item) => {
      if (item.properties.id === id) {
        item.properties.watchlist = false;
      }
    });
    setActiveNode(newGraphData.nodes.find((item) => item.properties.id === id));
    setGraphData(newGraphData);
  };

  const addInWatchlist = async (id, label) => {
    await api.post(`${API_ENDPOINT_V1}/graph/watchlist-add`, {
      id,
      label,
      projectId,
    });
    const newGraphData = { ...graphData };
    newGraphData.nodes.forEach((item) => {
      if (item.properties.id === id) {
        item.properties.watchlist = true;
      }
    });
    setActiveNode(newGraphData.nodes.find((item) => item.properties.id === id));
    setGraphData(newGraphData);
  };

  const changeDimension = async () => {
    setIs3DActive((b) => !b);
    await fetchAllData();
  };

  const zoomIn = () => {
    const { x, y, k } = currentGraphPosition;
    setCameraPosition(x, y, k + k / 5);
  };

  const zoomOut = () => {
    const { x, y, k } = currentGraphPosition;
    setCameraPosition(x, y, k - k / 5);
  };

  const setNodesToInitialCoordinates = () => {
    setSpecificNode('');
    setCameraPosition(-2.5, -0.833, 1.5);
    setGraphData((data) => ({
      ...data,
      nodes: data.nodes.map((node) => {
        const initialNode = initialNodeCoordinates.find(
          (initialNode) => initialNode.nodeIndex === node.index
        );
        node.x = initialNode.x;
        node.y = initialNode.y;
        node.fx = undefined;
        node.fy = undefined;
        return node;
      }),
    }));
  };

  const deleteCompanyNode = async (companyId) => {
    await api.delete(
      `${API_ENDPOINT_V1}/company/${currentProjectId}/delete/${encodeURIComponent(
        companyId
      )}`
    );
    await fetchAllData();
  };

  const deletePersonNode = async (node) => {
    const id = node.properties.id;
    const apiValue = node.properties.source;

    await api.delete(
      `${API_ENDPOINT_V1}/person/${currentProjectId}/delete/${encodeURIComponent(
        id
      )}/${apiValue}`
    );
    await fetchAllData();
  };

  const value = {
    fetchAllData,
    deleteCompanyNode,
    deletePersonNode,
    labelColors,
    background3D,
    background2D,
    wrapperContainerHeight,
    wrapperContainerWidth,
    currentProjectId,
    isLoading,
    fg2DRef,
    is3DActive,
    setIs3DActive,
    changeDimension,
    graphData,
    activeNode, // Appears on Side Params
    setActiveNode,
    specificNode, // Search Node ?
    handleGoDeep,
    removeFromWatchlist,
    addInWatchlist,
    setCurrentGraphPosition,
    zoomIn,
    zoomOut,
    setNodesToInitialCoordinates,
    initialNodeCoordinates,
    currentProjectName,
    setCurrentProjectName,
    // specificNodeAlert,
    // setSpecificNodeAlert,
    // resetSpecificNodeAlert: () => setSpecificNodeAlert(""),
    setCameraPosition,
    triggerActiveNodeFocus,
    setTriggerActiveNodeFocus,
    // anchorEl,
    // setAnchorEl,
    // open,
    // setGraphData,
    setSpecificNode,
    setSpecificNodeFromSearch,
    // setIsLoading,
    // fetchAllData,
  };

  return (
    <GraphContext.Provider value={value}>{children}</GraphContext.Provider>
  );
};
